<template>
  <v-dialog :value="value" persistent max-width="720" @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ $t('crudLinhas.detail.groupAllocation.title') }}</v-card-title>
      <v-card-subtitle v-if="linha">
        {{ linha.cod_linha }} — {{ linha.nome_linha }}
      </v-card-subtitle>
      <v-card-text>
        <LazySelect
          ref="grupoSelect"
          class="mb-3"
          get-url="getGruposlazy"
          text="nome"
          value="id_grupo"
          :label="$t('crudLinhas.detail.passengers.group')"
          :disabled="loading || allocating"
          @input="onGrupoSelect"
        />

        <v-alert v-if="verified && !membros.length" type="info" dense outlined class="mb-3">
          {{ $t('crudLinhas.detail.groupAllocation.emptyGroup') }}
        </v-alert>

        <v-alert
          v-else-if="verified && !elegiveis.length"
          type="warning"
          dense
          outlined
          class="mb-3"
        >
          {{ $t('crudLinhas.detail.groupAllocation.noneEligible') }}
        </v-alert>

        <v-alert
          v-else-if="verified && elegiveis.length"
          type="success"
          dense
          outlined
          class="mb-3"
        >
          {{ $t('crudLinhas.detail.groupAllocation.eligibleCount', { count: elegiveis.length }) }}
        </v-alert>

        <v-data-table
          v-if="membros.length"
          :headers="headers"
          :items="membros"
          :loading="loading"
          dense
          :items-per-page="10"
          item-key="id_pessoa"
          class="elevation-1"
        >
          <template v-slot:item.elegivel="{ item }">
            <v-chip x-small :color="item.elegivel ? 'success' : 'grey'" text-color="white">
              {{
                item.elegivel
                  ? $t('crudLinhas.detail.groupAllocation.eligible')
                  : item.motivo
              }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="loading || allocating" @click="close">
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          outlined
          class="mr-2"
          :loading="loading"
          :disabled="!idGrupo || allocating"
          @click="verificar"
        >
          {{ $t('crudLinhas.detail.groupAllocation.verify') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="allocating"
          :disabled="!elegiveis.length || loading"
          @click="alocar"
        >
          {{ $t('crudLinhas.detail.groupAllocation.allocate') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "@/utils/eventBus";
import LazySelect from "@/components/Selects/LazySelect.vue";
import { isValidado } from "@/utils/situacoesValidacao";

export default {
  name: "AlocaGrupoLinhaDialog",
  components: { LazySelect },
  props: {
    value: { type: Boolean, default: false },
    linha: { type: Object, default: null },
    historicoSessaoId: { type: String, default: null },
    historicoSessaoOrigemId: { type: String, default: null },
  },
  data() {
    return {
      idGrupo: null,
      membros: [],
      loading: false,
      allocating: false,
      verified: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("crudLinhas.detail.passengers.name"), value: "nome_completo" },
        { text: this.$t("validationSituation.headers.situation"), value: "nome_situacao_validacao" },
        {
          text: this.$t("crudLinhas.detail.groupAllocation.eligibility"),
          value: "elegivel",
          sortable: false,
        },
      ];
    },
    elegiveis() {
      return this.membros.filter((m) => m.elegivel);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.idGrupo = null;
        this.membros = [];
        this.verified = false;
        this.$nextTick(() => {
          const ref = this.$refs.grupoSelect;
          if (ref && typeof ref.resetData === "function") {
            ref.resetData();
            ref.loadLazyItems("");
          }
        });
      }
    },
  },
  methods: {
    onGrupoSelect(val) {
      this.idGrupo = val;
      this.verificar();
    },
    close() {
      this.$emit("input", false);
    },
    classifyMember(p) {
      const idLinha = this.linha && this.linha.id_linha != null ? Number(this.linha.id_linha) : null;
      const validado = isValidado(p);
      const idIda =
        p.id_linha_ida != null
          ? Number(p.id_linha_ida)
          : p.id_rota != null
            ? Number(p.id_rota)
            : null;
      const idVolta = p.id_linha_volta != null ? Number(p.id_linha_volta) : null;
      const onIda = idIda != null && idLinha != null && idIda === idLinha;
      const onVolta = idVolta != null && idLinha != null && idVolta === idLinha;
      const hasOther =
        (idIda != null && idLinha != null && idIda !== idLinha) ||
        (idVolta != null && idLinha != null && idVolta !== idLinha);

      if (!validado) {
        return {
          ...p,
          elegivel: false,
          motivo: this.$t("crudLinhas.detail.groupAllocation.notValidated"),
        };
      }
      if (onIda || onVolta) {
        return {
          ...p,
          elegivel: false,
          motivo: this.$t("crudLinhas.detail.groupAllocation.alreadyOnLine"),
        };
      }
      if (hasOther) {
        return {
          ...p,
          elegivel: false,
          motivo: this.$t("crudLinhas.detail.groupAllocation.otherLine"),
        };
      }
      return {
        ...p,
        elegivel: true,
        motivo: "",
      };
    },
    async verificar() {
      if (!this.idGrupo) {
        this.membros = [];
        this.verified = false;
        return;
      }
      this.loading = true;
      this.verified = false;
      try {
        const res = await apiController.request(apiController.endpoints.getPessoasporgrupo, {
          data: [this.idGrupo],
          silent: true,
        });
        const rows = Array.isArray(res.data) ? res.data : [];
        this.membros = rows.map((p) => this.classifyMember(p));
        this.verified = true;
      } catch (e) {
        this.membros = [];
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.loading = false;
      }
    },
    async alocar() {
      if (!this.elegiveis.length || !this.linha || this.linha.id_linha == null) return;
      this.allocating = true;
      try {
        const response = await apiController.request(apiController.endpoints.alocaPessoasemlinhas, {
          data: {
            passageiros: this.elegiveis.map((p) => ({
              id_pessoa: p.id_pessoa,
              // Default ambos = linha atual
              id_linha_ida: this.linha.id_linha,
              id_linha_volta: this.linha.id_linha,
              id_grupo: p.id_grupo != null ? p.id_grupo : this.idGrupo,
              id_cerca_embarque: p.id_cerca_embarque != null ? p.id_cerca_embarque : null,
              id_cerca_desembarque: p.id_cerca_desembarque != null ? p.id_cerca_desembarque : null,
            })),
            historico_sessao_id: this.historicoSessaoId || null,
            historico_sessao_origem_id: this.historicoSessaoOrigemId || null,
            historico_origem: "CRUD_LINHAS",
            historico_linha_id: this.linha && this.linha.id_linha,
          },
          silent: true,
        });
        if (response.data && response.data.historicoAviso) {
          EventBus.$emit("show-alert", {
            tipo: "warning",
            mensagem: this.$t("crudLinhas.messages.peopleHistorySaveError"),
          });
        }
        EventBus.$emit("show-success", {
          message: this.$t("crudLinhas.detail.groupAllocation.allocateSuccess", {
            count: this.elegiveis.length,
          }),
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        EventBus.$emit("show-error", { error: e });
      } finally {
        this.allocating = false;
      }
    },
  },
};
</script>
